import React, { useEffect, useState } from "react";
import { api, apiErrorMessage, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasRole, ROLE_LABELS } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, UsersThree, Trash } from "@phosphor-icons/react";

const EMPTY = { name: "", email: "", password: "", role: "staff", is_active: true };

export default function Users() {
    const { user } = useAuth();
    const canAdmin = hasRole(user, "admin");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/users");
            setItems(data);
        } catch (e) { toast.error(apiErrorMessage(e)); } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openCreate = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
    const openEdit = (u) => { setEditing(u); setForm({ ...EMPTY, ...u, password: "" }); setDialogOpen(true); };

    const submit = async () => {
        try {
            if (editing) {
                const patch = { name: form.name, role: form.role, is_active: form.is_active };
                if (form.password) patch.password = form.password;
                await api.patch(`/users/${editing.id}`, patch);
                toast.success("User updated");
            } else {
                if (!form.email) return toast.error("Email required");
                await api.post("/users", form);
                toast.success("User created" + (form.password ? "" : " · Google sign-in enabled"));
            }
            setDialogOpen(false); load();
        } catch (e) { toast.error(apiErrorMessage(e)); }
    };

    const remove = async (u) => {
        if (!window.confirm(`Delete ${u.email}?`)) return;
        try {
            await api.delete(`/users/${u.id}`);
            toast.success("User deleted");
            load();
        } catch (e) { toast.error(apiErrorMessage(e)); }
    };

    return (
        <div className="space-y-6" data-testid="users-page">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <div className="eyebrow mb-2">Access</div>
                    <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight font-display">Team & Roles</h1>
                    <p className="text-sm text-neutral-500 mt-1.5">{items.length} team member{items.length !== 1 && "s"} with role-based access.</p>
                </div>
                {canAdmin && (
                    <Button onClick={openCreate} className="text-white h-11" style={{ backgroundColor: "#15803D" }} data-testid="users-add-btn">
                        <Plus size={16} weight="bold" className="mr-1.5" /> New User
                    </Button>
                )}
            </div>

            <div className="bg-white border border-neutral-200 rounded-md overflow-hidden" data-testid="users-table">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                            <tr>
                                <th className="text-left px-5 py-3 font-semibold">Name</th>
                                <th className="text-left px-5 py-3 font-semibold">Email</th>
                                <th className="text-left px-5 py-3 font-semibold">Role</th>
                                <th className="text-left px-5 py-3 font-semibold">Status</th>
                                <th className="text-left px-5 py-3 font-semibold">Joined</th>
                                <th className="px-5 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={6} className="text-center py-10 text-neutral-400">Loading...</td></tr>}
                            {!loading && items.length === 0 && (
                                <tr><td colSpan={6} className="text-center py-14 text-neutral-400">
                                    <UsersThree size={30} weight="thin" className="mx-auto mb-2 text-neutral-300" />
                                    No users yet.
                                </td></tr>
                            )}
                            {items.map((u) => (
                                <tr key={u.id} className="data-row border-t border-neutral-100" data-testid={`user-row-${u.email}`}>
                                    <td className="px-5 py-3 font-semibold">{u.name}</td>
                                    <td className="px-5 py-3 text-neutral-600">{u.email}</td>
                                    <td className="px-5 py-3">
                                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded"
                                              style={{ background: "#DCFCE7", color: "#15803D" }}>
                                            {ROLE_LABELS[u.role]}
                                        </span>
                                    </td>
                                    <td className="px-5 py-3">
                                        {u.is_active ? (
                                            <span className="text-xs text-green-700 font-semibold">Active</span>
                                        ) : (
                                            <span className="text-xs text-neutral-500">Disabled</span>
                                        )}
                                    </td>
                                    <td className="px-5 py-3 text-xs text-neutral-500 whitespace-nowrap">{formatDate(u.created_at)}</td>
                                    <td className="px-5 py-3 text-right whitespace-nowrap">
                                        {canAdmin && (
                                            <>
                                                <button onClick={() => openEdit(u)} className="text-neutral-500 hover:text-neutral-900 mr-3" data-testid={`user-edit-${u.email}`}>
                                                    <Pencil size={14} />
                                                </button>
                                                {u.id !== user.id && (
                                                    <button onClick={() => remove(u)} className="text-red-500 hover:text-red-700" data-testid={`user-delete-${u.email}`}>
                                                        <Trash size={14} />
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent data-testid="user-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-extrabold font-display">{editing ? "Edit User" : "New User"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Full Name</Label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="user-form-name" />
                        </div>
                        {!editing && (
                            <div>
                                <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Email</Label>
                                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="user-form-email" />
                            </div>
                        )}
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">
                                Password {editing ? "(leave blank to keep unchanged)" : "(leave blank for Google sign-in only)"}
                            </Label>
                            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" data-testid="user-form-password" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Role</Label>
                            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                                <SelectTrigger className="mt-1.5" data-testid="user-form-role"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="admin">Admin</SelectItem>
                                    <SelectItem value="manager">Manager</SelectItem>
                                    <SelectItem value="staff">Staff</SelectItem>
                                    <SelectItem value="cashier">Cashier</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {editing && (
                            <div className="flex items-center gap-3">
                                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="user-form-active" />
                                <Label>Active</Label>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="user-form-cancel">Cancel</Button>
                        <Button onClick={submit} className="text-white" style={{ backgroundColor: "#15803D" }} data-testid="user-form-submit">
                            {editing ? "Save Changes" : "Create User"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
